import AgreementDocumentView from './components/service/AgreementDocumentView';
import './components/records/recordEditPopGuard';
import React from 'react';
import './ui/NativeTextarea.css';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigationType } from 'react-router-dom';
import type { SupabaseClient, Session } from '@supabase/supabase-js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AppShell from './ui/AppShell';
import { GlobalErrorBoundary } from './ui/GlobalErrorBoundary';
import TopLoader from './ui/TopLoader';
import { installGlobalDebugCapture } from './lib/installGlobalDebugCapture';
import { installPerfObservers, startSpan, endSpan } from './lib/perf';
import HomePage from './pages/HomePage';
import LoginPage from './pages/auth/LoginPage';
import GuestAccountSecurityPage from './pages/auth/GuestAccountSecurityPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import { JobHubProvider } from './jobs/JobHub';
import './global.css';
// Vercel Web Analytics (framework-agnostic React integration)
import { Analytics } from '@vercel/analytics/react';
// Vercel Speed Insights (React SPA)
import { SpeedInsights } from '@vercel/speed-insights/react';
import LoadingScreen from './ui/LoadingScreen';
import useSessionStore from './state/session';
import { getSiteAccessState } from './state/siteAccess';
import { useRoles } from './hooks/useRoles';
import SiteAccessRequired from './components/auth/SiteAccessRequired';
import { initInteractionTracker } from './lib/interactionTracker';
import { useReporterContextBridge } from './lib/reporterContextBridge';
import { useModalScrollLock } from './hooks/useModalScrollLock';
import AuthCallback from './pages/auth/AuthCallback';
import supabaseClient, { SUPABASE_AUTH_STORAGE_KEY } from './lib/supabaseClient';
import { MenuButtons } from './lib/menuButtons';
import { installEditModalEnhancer } from './utils/editModalStickyEnhancer';
import { installHeroStatsToggleEnhancer } from './utils/heroStatsToggleEnhancer';
import { installTelemetryFlush, setTelemetryUserId } from './lib/telemetryFlush';
import { recordRealtimeEvent } from './lib/realtimeTelemetry';
import { isSameEventWorkspaceNavigation, isSameScheduleNavigation } from './lib/navigationScroll';
import {
  MAINTENANCE_ROUTE_FEATURES,
} from './domain/security/featurePermissions';
import type { FeaturePermissionLevel, PagePermissionLevel } from './types/security';
import EventsPage from './pages/EventsPage';
import InstallationsPage from './pages/installations/InstallationsPage';
import InstallationRouteGate from './pages/installations/InstallationRouteGate';
import TransferOrdersPage from './pages/transfers/TransferOrdersPage';
import TransferOrderDetail from './pages/transfers/TransferOrderDetail';
import LegacyProcurementRedirect from './pages/transfers/LegacyProcurementRedirect';
import LegacyInventoryRedirect from './pages/inventory/LegacyInventoryRedirect';
import { AuthSessionCoordinator, requestSessionRetry } from './auth/AuthSessionCoordinator';
import { isProtectedAppPath } from './auth/routePolicy';
import { setActivePrincipalQueryClient } from './auth/queryClientLifecycle';
import { runSupabaseQuery, safeErrorMessage } from './utils/appErrors';
import { shouldRetryPrincipalQuery } from './lib/queryRetry';
import { installProtectedAppFetchGuard } from './auth/protectedFetchGuard';
import { setAuthTransportExpectation } from './auth/sessionSignals';
import ClientPortalRouteGate from './auth/ClientPortalRouteGate';
import { PageTitleProvider } from './ui/pageTitle';

const VenuesPage = React.lazy(() => import('./pages/VenuesPage'));
const VenueDetail = React.lazy(() => import('./pages/VenueDetail'));
const DebugOverlay = React.lazy(() => import('./DebugOverlay'));
const EventDetail = React.lazy(() => import('./pages/EventDetail'));
const InstallationDetailPage = React.lazy(() => import('./pages/installations/InstallationDetailPage'));
const PurchaseOrderRecordPage = React.lazy(() => import('./pages/transfers/purchaseOrders/PurchaseOrderRecordPage'));
const SubrentalRecordPage = React.lazy(() => import('./pages/transfers/subrentals/SubrentalRecordPage'));
const InstallationSignPage = React.lazy(() => import('./pages/installations/InstallationSignPage'));
const InstallationCompletionSignPage = React.lazy(() => import('./pages/installations/InstallationCompletionSignPage'));
const InstallationClientViewPage = React.lazy(() => import('./pages/installations/InstallationClientViewPage'));
const EventClientViewPage = React.lazy(() => import('./pages/event/EventClientViewPage'));
const LaborCalendarPage = React.lazy(() => import('./pages/LaborCalendarPage'));
const ScheduleTimeReportPage = React.lazy(() => import('./pages/ScheduleTimeReportPage'));
const ScheduleGuestLinkPage = React.lazy(() => import('./pages/ScheduleGuestLinkPage'));
const ClientPortalDashboard = React.lazy(() => import('./pages/client-portal/ClientPortalDashboard'));
const ClientPortalLanding = React.lazy(() => import('./pages/client-portal/ClientPortalLanding'));
const ClientPortalSummaryInvoice = React.lazy(() => import('./pages/client-portal/SummaryInvoiceView'));
const ServiceTicketPortalView = React.lazy(() => import('./pages/client-portal/ServiceTicketPortalView'));
const OrderChatPopoutPage = React.lazy(() => import('./pages/event/order/OrderChatPopoutPage'));
const ClientsPage = React.lazy(() => import('./pages/ClientsPage'));
const ClientDetail = React.lazy(() => import('./pages/ClientDetail'));
const ChangeLogPage = React.lazy(() => import('./pages/ChangeLogPage'));
const UserProfilePage = React.lazy(() => import('./pages/UserProfilePage'));
const EditProfilePage = React.lazy(() => import('./pages/EditProfilePage'));
const FreelancerProfilePage = React.lazy(() => import('./pages/FreelancerProfilePage'));
const UserTodoPage = React.lazy(() => import('./pages/UserTodoPage'));
const SuperadminTodoPage = React.lazy(() => import('./pages/SuperadminTodoPage'));
const ServiceTicketsPage = React.lazy(() => import('./pages/service/ServiceTicketsPage'));
const ServiceTicketDetail = React.lazy(() => import('./pages/service/ServiceTicketDetail'));
const ServiceScanPage = React.lazy(() => import('./pages/service/ServiceScanPage'));
const ServiceTicketCreatePage = React.lazy(() => import('./pages/service/ServiceTicketCreatePage'));
const SignatureDocumentsPage = React.lazy(() => import('./pages/signatureDocuments/SignatureDocumentsPage'));
const SignatureDocumentEditorPage = React.lazy(() => import('./pages/signatureDocuments/SignatureDocumentEditorPage'));
const PublicSignatureDocumentPage = React.lazy(() => import('./pages/signatureDocuments/PublicSignatureDocumentPage'));
const SignatureDocumentSignInPersonPage = React.lazy(() => import('./pages/signatureDocuments/SignatureDocumentSignInPersonPage'));
const PublicTemplateLandingPage = React.lazy(() => import('./pages/signatureDocuments/PublicTemplateLandingPage'));
const PublicPaymentPage = React.lazy(() => import('./pages/payments/PublicPaymentPage'));
const PublicVenuePrecontractIntakePage = React.lazy(() => import('./pages/venue/PublicVenuePrecontractIntakePage'));
const PublicPostEventPage = React.lazy(() => import('./pages/postEvent/PublicPostEventPage'));
const PostEventPreviewPage = React.lazy(() => import('./pages/postEvent/PostEventPreviewPage'));
const ServiceTicketAcceptancePage = React.lazy(() => import('./pages/service/ServiceTicketAcceptancePage'));
const YearlyInventoryCampaignsPage = React.lazy(() => import('./pages/yearlyInventory/YearlyInventoryCampaignsPage'));
const YearlyInventoryWorkspacePage = React.lazy(() => import('./pages/yearlyInventory/YearlyInventoryWorkspacePage'));
const YearlyInventoryReviewPage = React.lazy(() => import('./pages/yearlyInventory/YearlyInventoryReviewPage'));
const YearlyInventoryPlannedPurchasesPage = React.lazy(() => import('./pages/yearlyInventory/YearlyInventoryPlannedPurchasesPage'));
const YearlyInventoryPlannedTransfersPage = React.lazy(() => import('./pages/yearlyInventory/YearlyInventoryPlannedTransfersPage'));
const YearlyInventoryRollupPage = React.lazy(() => import('./pages/yearlyInventory/YearlyInventoryRollupPage'));
const InventoryLibraryPage = React.lazy(() => import('./pages/inventory/InventoryLibraryPage'));
const CountRecordPage = React.lazy(() => import('./pages/inventory/counts/CountRecordPage'));
const ItemRecordPage = React.lazy(() => import('./pages/inventory/items/ItemRecordPage'));
const KitRecordPage = React.lazy(() => import('./pages/inventory/kits/KitRecordPage'));
const AssetRecordPage = React.lazy(() => import('./pages/inventory/assets/AssetRecordPage'));
const InventoryRunDetailPage = React.lazy(() => import('./pages/inventoryRuns/InventoryRunDetailPage'));
const AdminUsersPage = React.lazy(() => import('./pages/AdminUsersPage'));
const AdminAISettingsPage = React.lazy(() => import('./pages/AdminAISettingsPage'));
const AdminProductsPage = React.lazy(() => import('./pages/admin/AdminProductsPage'));
const AdminProductEditorPage = React.lazy(() => import('./pages/admin/AdminProductEditorPage'));
const AdminItemsPage = React.lazy(() => import('./pages/admin/AdminItemsPage'));
const AdminInstallationCatalogPage = React.lazy(() => import('./pages/admin/AdminInstallationCatalogPage'));
const AdminNonSerialItemsPage = React.lazy(() => import('./pages/admin/AdminNonSerialItemsPage'));
const AdminItemEditorPage = React.lazy(() => import('./pages/admin/AdminItemEditorPage'));
const AdminLaborPage = React.lazy(() => import('./pages/admin/AdminLaborPage'));
const AdminServicePage = React.lazy(() => import('./pages/admin/AdminServicePage'));
const AdminAssetEditorPage = React.lazy(() => import('./pages/admin/AdminAssetEditorPage'));
const AdminCategoryEditorPage = React.lazy(() => import('./pages/admin/AdminCategoryEditorPage'));
const AdminLocationsPage = React.lazy(() => import('./pages/admin/AdminLocationsPage'));
const AdminLocationEditorPage = React.lazy(() => import('./pages/admin/AdminLocationEditorPage'));
const AdminCategoriesPage = React.lazy(() => import('./pages/admin/AdminCategoriesPage'));
const AdminSectionsPage = React.lazy(() => import('./pages/admin/AdminSectionsPage'));
const AdminItemsPanelColumnsPage = React.lazy(() => import('./pages/admin/AdminItemsPanelColumnsPage'));
const AdminFormsPage = React.lazy(() => import('./pages/admin/AdminFormsPage'));
const AdminFormBuilderPage = React.lazy(() => import('./pages/admin/AdminFormBuilderPage'));
const AdminVenuePrecontractFormPage = React.lazy(() => import('./pages/admin/AdminVenuePrecontractFormPage'));
const AdminMaintenancePage = React.lazy(() => import('./pages/admin/AdminMaintenancePage'));
const MaintenanceLayout = React.lazy(() => import('./pages/admin/MaintenanceLayout'));
const AdminTaxCodesPage = React.lazy(() => import('./pages/admin/AdminTaxCodesPage'));
const AdminTermsAndConditionsPage = React.lazy(() => import('./pages/admin/AdminTermsAndConditionsPage'));
const AdminPaymentTermsPage = React.lazy(() => import('./pages/admin/AdminPaymentTermsPage'));
const AdminFreelancersPage = React.lazy(() => import('./pages/admin/AdminFreelancersPage'));
const AdminChargesPage = React.lazy(() => import('./pages/admin/AdminChargesPage'));
const AdminPricingMaintenancePage = React.lazy(() => import('./pages/admin/AdminPricingMaintenancePage'));
const AdminAccountingGlPage = React.lazy(() => import('./pages/admin/AdminAccountingGlPage'));
const InvoicingPage = React.lazy(() => import('./pages/InvoicingPage'));
const InvoiceRecordPage = React.lazy(() => import('./pages/invoicing/invoice/InvoiceRecordPage'));
const MasterBillRecordPage = React.lazy(() => import('./pages/invoicing/masterBill/MasterBillRecordPage'));
const AdminVendorsPage = React.lazy(() => import('./pages/admin/AdminVendorsPage'));

// Mount function (for WP plugin widget mode)
// Expect global SiteSurveyConfig or data-* attributes for config

// Dev auth bypass: create a mock session for local development
// Only used when VITE_DEV_AUTH_EMAIL is set and running in dev mode
function createDevSession(authUserId: string, email: string): Session {
  return {
    access_token: 'dev-bypass-token',
    refresh_token: 'dev-bypass-refresh',
    expires_in: 999999,
    expires_at: Math.floor(Date.now() / 1000) + 999999,
    token_type: 'bearer',
    user: {
      id: authUserId,
      email: email,
      app_metadata: { provider: 'azure', roles: [] },
      user_metadata: {},
      aud: 'authenticated',
      created_at: new Date().toISOString(),
    },
  } as Session;
}

// Check if dev auth bypass should be used
const DEV_AUTH_EMAIL = import.meta.env.DEV ? (import.meta.env.VITE_DEV_AUTH_EMAIL as string | undefined) : undefined;
// Password for dev login (use signInWithPassword for a REAL session - most reliable option)
const DEV_AUTH_PASSWORD = import.meta.env.DEV ? (import.meta.env.VITE_DEV_AUTH_PASSWORD as string | undefined) : undefined;
// Optional: provide auth_user_id directly if RLS blocks unauthenticated lookup
const DEV_AUTH_USER_ID = import.meta.env.DEV ? (import.meta.env.VITE_DEV_AUTH_USER_ID as string | undefined) : undefined;

interface BootConfig { supabaseUrl: string; supabaseAnonKey: string; }

function createPrincipalQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: shouldRetryPrincipalQuery,
      },
    },
  });
}

function RouteLoadingFallback() {
  return (
    <div className="section-shell">
      <p className="fg-muted text-sm">Loading...</p>
    </div>
  );
}

function ScrollToTopOnNavigate() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const isFirstPaintRef = React.useRef(true);
  const previousPathnameRef = React.useRef<string | null>(null);
  const latestScrollYRef = React.useRef(typeof window === 'undefined' ? 0 : window.scrollY);

  React.useEffect(() => {
    const captureScrollPosition = () => {
      latestScrollYRef.current = window.scrollY;
    };
    window.addEventListener('scroll', captureScrollPosition, { passive: true });
    return () => window.removeEventListener('scroll', captureScrollPosition);
  }, []);

  React.useLayoutEffect(() => {
    const previousPathname = previousPathnameRef.current;
    const sameSchedule = isSameScheduleNavigation(previousPathname, location.pathname);
    previousPathnameRef.current = location.pathname;

    // Preserve native anchor behavior when hash links are present.
    if (location.hash) {
      isFirstPaintRef.current = false;
      return;
    }
    const shouldReset = isFirstPaintRef.current || navigationType !== 'POP';
    isFirstPaintRef.current = false;
    if (!shouldReset) return;
    if (isSameEventWorkspaceNavigation(previousPathname, location.pathname)
      || sameSchedule) {
      // Native focus/bring-into-view can move Schedule before a scroll event
      // reaches the listener. Its actual position belongs to this navigation.
      const preservedScrollY = sameSchedule ? window.scrollY : latestScrollYRef.current;
      latestScrollYRef.current = preservedScrollY;
      window.scrollTo({ top: preservedScrollY, left: window.scrollX, behavior: 'auto' });
      const frame = window.requestAnimationFrame(() => {
        window.scrollTo({ top: preservedScrollY, left: window.scrollX, behavior: 'auto' });
      });
      return () => window.cancelAnimationFrame(frame);
    }
    latestScrollYRef.current = 0;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search, location.hash, navigationType]);

  return null;
}

function Protected({ children }: { children: React.ReactNode }) {
  const session = useSessionStore(s=>s.session);
  const authStatus = useSessionStore(s=>s.authStatus);
  const contextStatus = useSessionStore(s=>s.contextStatus);
  const authIssue = useSessionStore(s=>s.authIssue);
  const sessionReady = contextStatus === 'ready';
  const securityLevel = useSessionStore(s=>s.securityLevel);
  const accessConfigured = useSessionStore(s=>s.accessConfigured);
  const allowedLocationIds = useSessionStore(s=>s.allowedLocationIds);
  const { isArchived, isFreelancer } = useRoles();
  const location = useLocation();
  if (authStatus === 'checking' || contextStatus === 'loading') return null;
  if (authStatus === 'unavailable') {
    return <SessionRecoveryState message={authIssue?.message} />;
  }
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname + location.search + location.hash }} />;
  if (contextStatus === 'error') {
    if (authIssue?.kind === 'access_denied') {
      return <AccessDeniedState />;
    }
    return <SessionRecoveryState message={authIssue?.message} />;
  }
  if (authStatus !== 'authenticated' || !sessionReady) return null;
  if (isArchived) {
    return (
      <div className="section-shell stack gap-md" style={{maxWidth:560, margin:'2rem auto'}}>
        <h2 className="m0">Access Removed</h2>
        <p className="text-sm fg-muted" style={{margin:0}}>Your account has been archived and no longer has access to the application. If you believe this is a mistake, please contact an administrator.</p>
      </div>
    );
  }
  if (!accessConfigured || !securityLevel) {
    return <AccessNotConfiguredState />;
  }
  if (['user', 'manager'].includes(securityLevel)) {
    const siteAccess = getSiteAccessState({
      sessionReady,
      securityLevel,
      allowedLocationIds,
    });
    if (siteAccess.isBlocked) {
      return <SiteAccessRequired />;
    }
  }
  if (isFreelancer) {
    const allowed = ['/', '/freelancer/profile', '/account/security', '/schedule', '/calendar', '/login'];
    const current = location.pathname;
    if (!allowed.some(p => current === p || current.startsWith(p + '?'))) {
      return <Navigate to="/" replace />;
    }
  }
  if (securityLevel === 'guest' && !isFreelancer && !['/', '/account/security'].includes(location.pathname)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function SessionRecoveryState({ message }: { message?: string }) {
  return (
    <div className="section-shell stack gap-md" style={{ maxWidth: 560, margin: '2rem auto' }}>
      <h2 className="m0">We could not verify your session</h2>
      <p className="text-sm fg-muted" style={{ margin: 0 }}>
        {message || 'The service is temporarily unavailable. Your session was kept.'}
      </p>
      <div>
        <button type="button" className="btn btn-primary" onClick={requestSessionRetry}>
          Try again
        </button>
      </div>
    </div>
  );
}

function BackgroundSessionWarning() {
  const authStatus = useSessionStore((state) => state.authStatus);
  const contextStatus = useSessionStore((state) => state.contextStatus);
  const authIssue = useSessionStore((state) => state.authIssue);
  if (authStatus !== 'authenticated' || contextStatus !== 'ready' || !authIssue) return null;

  return (
    <div className="alert alert-warning app-session-warning" role="status" aria-live="polite">
      <span>
        We could not verify your session in the background. Your work is still open.
      </span>
      <button type="button" className="btn xs" onClick={requestSessionRetry}>
        Try again
      </button>
    </div>
  );
}

function AccessDeniedState() {
  return (
    <div className="section-shell stack gap-md" style={{ maxWidth: 560, margin: '2rem auto' }}>
      <h2 className="m0">Access denied</h2>
      <p className="text-sm fg-muted" style={{ margin: 0 }}>
        You do not have permission to open this information.
      </p>
    </div>
  );
}

function AccessNotConfiguredState() {
  return (
    <div className="section-shell stack gap-md" style={{ maxWidth: 560, margin: '2rem auto' }}>
      <h2 className="m0">Access not configured</h2>
      <p className="text-sm fg-muted" style={{ margin: 0 }}>
        Your sign-in is valid, but this account does not have a security level yet. Ask an administrator to finish the account setup.
      </p>
    </div>
  );
}

function HomeRoute() {
  const securityLevel = useSessionStore((state) => state.securityLevel);
  if (securityLevel === 'guest') return <HomePage />;
  return <PageProtected pageKey="dashboard"><HomePage /></PageProtected>;
}

function PageProtected({
  pageKey,
  required = 'view',
  children,
}: {
  pageKey: string;
  required?: Extract<PagePermissionLevel, 'view' | 'edit'>;
  children: React.ReactNode;
}) {
  const { getPagePermission } = useRoles();
  const rank: Record<PagePermissionLevel, number> = { none: 0, view: 1, edit: 2 };
  if (rank[getPagePermission(pageKey)] < rank[required]) {
    return <AccessDeniedState />;
  }
  return <>{children}</>;
}

function FeatureProtected({
  featureKey,
  required = 'view',
  children,
}: {
  featureKey: string;
  required?: Exclude<FeaturePermissionLevel, 'none'>;
  children: React.ReactNode;
}) {
  const { getFeaturePermission } = useRoles();
  const rank: Record<FeaturePermissionLevel, number> = { none: 0, view: 1, edit_own: 2, edit_all: 3 };
  if (rank[getFeaturePermission(featureKey)] < rank[required]) {
    return <AccessDeniedState />;
  }
  return <>{children}</>;
}

// The suggestions inbox now lives in the Work Planner tab; keep old deep links
// (notification bell, suggestion follow-up tasks) working.
function SuggestionsInboxRedirect() {
  const location = useLocation();
  const suggestionId = new URLSearchParams(location.search).get('suggestionId');
  const target = suggestionId
    ? `/todo?tab=suggestions&suggestionId=${encodeURIComponent(suggestionId)}`
    : '/todo?tab=suggestions';
  return <Navigate to={target} replace />;
}

function Boot({ cfg }: { cfg: BootConfig }) {
  const setSession = useSessionStore(s=>s.setSession);
  const booting = useSessionStore(s=>s.booting);
  const setBooting = useSessionStore(s=>s.setBooting);
  const profileId = useSessionStore(s=>s.profileId);
  useReporterContextBridge();
  const supabase: SupabaseClient = supabaseClient;
  const session = useSessionStore(s=>s.session);
  const authStatus = useSessionStore(s=>s.authStatus);
  const contextStatus = useSessionStore(s=>s.contextStatus);
  const currentLocation = useLocation();
  React.useEffect(() => {
    setTelemetryUserId(session?.user?.id ?? null);
  }, [session]);

  const loadContext = useSessionStore(s=>s.loadContext);
  const showLoadingScreen = Boolean(
    !DEV_AUTH_EMAIL
    && isProtectedAppPath(currentLocation.pathname)
    && (authStatus === 'checking' || contextStatus === 'loading'),
  );
  React.useEffect(() => {
    MenuButtons.init();
  }, []);
  React.useEffect(() => {
    if (booting && session) {
      setBooting(false);
    }
  }, [booting, session, setBooting]);
  React.useEffect(() => {
    initInteractionTracker();
  }, []);
  React.useEffect(() => {
    installEditModalEnhancer();
  }, []);
  React.useEffect(() => {
    installHeroStatsToggleEnhancer();
  }, []);
  // No global modal open state here; pass false so type signature is satisfied
  useModalScrollLock(false);
  // booting state is managed in zustand for global visibility
  // Clear stale/invalid refresh tokens from previous runs to avoid noisy 400 errors
  const clearSbAuthStorage = React.useCallback(() => {
    try {
      if (typeof window === 'undefined') return;
      if (SUPABASE_AUTH_STORAGE_KEY) {
        window.localStorage.removeItem(SUPABASE_AUTH_STORAGE_KEY);
      }
    } catch { /* noop */ }
  }, []);

  React.useEffect(() => {
    if (!DEV_AUTH_EMAIL) return undefined;
    let active = true;
    (async () => {
      setBooting(true);

      // Dev auth bypass: auto-login when VITE_DEV_AUTH_EMAIL is set in dev mode
      if (DEV_AUTH_EMAIL) {
        startSpan('auth.devBypass');

        // PREFERRED: Use password login if VITE_DEV_AUTH_PASSWORD is set
        // This provides a REAL Supabase session that works with all API calls
        if (DEV_AUTH_PASSWORD) {
          // eslint-disable-next-line no-console
          console.warn(`[DEV AUTH BYPASS] Auto-logging in as ${DEV_AUTH_EMAIL} (using password)`);
          try {
            const { data, error } = await supabase.auth.signInWithPassword({
              email: DEV_AUTH_EMAIL,
              password: DEV_AUTH_PASSWORD,
            });

            if (!error && data?.session) {
              // eslint-disable-next-line no-console
              console.info('[DEV AUTH BYPASS] Password login successful - full API access enabled');
              setAuthTransportExpectation('session', data.session.access_token);
              setSession(data.session);
              if (active) setBooting(false);
              startSpan('context.load');
              void loadContext(supabase)
                .then(() => endSpan('context.load', { source: 'devBypass' }))
                .catch(() => endSpan('context.load', { source: 'devBypass', error: 'loadContext failed' }));
              endSpan('auth.devBypass', { success: true, email: DEV_AUTH_EMAIL, method: 'password' });
              return;
            }

            // eslint-disable-next-line no-console
            console.warn('[DEV AUTH BYPASS] Password login failed:', error?.message);
            // eslint-disable-next-line no-console
            console.warn('[DEV AUTH BYPASS] To enable password login: 1) Set a password for this user in Supabase Dashboard');
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[DEV AUTH BYPASS] Password login error:', err);
          }
        }

        // Local-only fallback. The server intentionally does not mint browser
        // sessions, even for development, so keep this path off the network.
        try {
          // eslint-disable-next-line no-console
          console.warn('[DEV AUTH BYPASS] Password login unavailable, using local mock (API calls may fail)');

          const authUserId: string | null = DEV_AUTH_USER_ID || null;
          const resolvedEmail = DEV_AUTH_EMAIL;

          if (authUserId) {
            const mockSession = createDevSession(authUserId, resolvedEmail);
            setAuthTransportExpectation('session', mockSession.access_token);
            setSession(mockSession);
            if (active) setBooting(false);
            startSpan('context.load');
            void loadContext(supabase)
              .then(() => endSpan('context.load', { source: 'devBypass' }))
              .catch(() => endSpan('context.load', { source: 'devBypass', error: 'loadContext failed' }));
            endSpan('auth.devBypass', { success: true, email: resolvedEmail, fallback: true });
            // Skip normal auth flow and listener setup - dev session is static
            return;
          }

          // eslint-disable-next-line no-console
          console.error('[DEV AUTH BYPASS] No auth_user_id available, falling through to normal auth');
          endSpan('auth.devBypass', { error: 'no_auth_user_id' });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('[DEV AUTH BYPASS] Error:', e);
          endSpan('auth.devBypass', { error: String(e) });
          // Fall through to normal auth flow
        }
      }

      try {
        startSpan('auth.getSession');
        const { data, error } = await supabase.auth.getSession();
        endSpan('auth.getSession', { hasSession: !!data?.session, error: error?.message });
        if (error && String(error.message||'').toLowerCase().includes('invalid refresh token')) {
          clearSbAuthStorage();
          await supabase.auth.signOut();
          setSession(null);
          if (active) setBooting(false);
          return;
        }
        setAuthTransportExpectation(
          data?.session ? 'session' : 'blocked',
          data?.session?.access_token ?? null,
        );
        setSession(data?.session || null);
        if (active) setBooting(false);
        if (data?.session) {
          startSpan('context.load');
          void loadContext(supabase).then(() => endSpan('context.load')).catch(() => endSpan('context.load', { error: 'loadContext failed' }));
        }
      } catch (err) {
        // getSession() can throw (e.g. Web Locks AbortError) instead of
        // returning { error }. Without this catch, booting stays true and
        // the user is stuck on "Loading workspace" forever — even after
        // refresh, because the poisoned token remains in localStorage.
        // eslint-disable-next-line no-console
        console.error('[boot] getSession threw — clearing auth storage and falling back to login:', err);
        endSpan('auth.getSession', { error: String(err) });
        clearSbAuthStorage();
        try { await supabase.auth.signOut(); } catch { /* best effort */ }
        setSession(null);
        if (active) setBooting(false);
      }
    })();

    // Skip auth state listener if using dev bypass (session is static)
    if (DEV_AUTH_EMAIL) {
      return () => { active = false; };
    }

    const { data: listener } = supabase.auth.onAuthStateChange(async (_ev, session) => {
      setSession(session);
      // Safety net: if the initial boot effect threw before reaching
      // setBooting(false), the auth listener is our second chance.
      if (useSessionStore.getState().booting) {
        setBooting(false);
      }
      if(session){
        startSpan('context.load');
        void loadContext(supabase).then(() => endSpan('context.load', { source: 'onAuthStateChange' })).catch(() => endSpan('context.load', { source: 'onAuthStateChange', error: 'loadContext failed' }));
      }
    });
    return () => { active = false; listener.subscription.unsubscribe(); };
  }, [supabase, setSession, loadContext, clearSbAuthStorage]);

  // Realtime: refresh roles and site access immediately when assignments change.
  React.useEffect(() => {
    // We need both a session and the resolved profileId to subscribe accurately
    const session = useSessionStore.getState().session;
    if (!session?.user?.id || !profileId) return;

    const channelName = `user-access-context:${profileId}`;
    const ch = supabase.channel(channelName);
    const handler = async (payload?: any) => {
      recordRealtimeEvent({
        source: 'main-user-access-context',
        table: payload?.table ?? 'user_access_context',
        eventType: payload?.eventType ?? '*',
      });
      try { await useSessionStore.getState().loadContext(supabase); } catch { /* noop */ }
    };

    ch.on('postgres_changes', { event: '*', schema: 'public', table: 'user_security_levels', filter: `user_profile_id=eq.${profileId}` }, handler)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_roles', filter: `user_profile_id=eq.${profileId}` }, handler)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_app_roles', filter: `user_profile_id=eq.${profileId}` }, handler)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'guest_resource_grants', filter: `grantee_profile_id=eq.${profileId}` }, handler)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_profile_locations', filter: `user_profile_id=eq.${profileId}` }, handler)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_profile_regions', filter: `user_profile_id=eq.${profileId}` }, handler)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'app_roles' }, handler)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'page_definitions' }, handler)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'feature_definitions' }, handler)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'app_page_permissions' }, handler)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'app_feature_permissions' }, handler)
      .subscribe();

    return () => { try { ch.unsubscribe(); } catch { /* noop */ } };
  }, [supabase, profileId]);

  const showDebug = typeof window !== 'undefined' && window.location.search.includes('debug=1');
  // Overlay now dev-only (?debug=1) and no longer toggleable; all detailed logs live in popout console.
  // Install global console/error capture once.
  React.useEffect(() => { installGlobalDebugCapture(); installPerfObservers(); installTelemetryFlush(); }, []);

  // Global hotkeys: Ctrl+Alt+D opens popout DebugConsole directly (overlay no longer toggleable).
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey || !e.altKey) return;
      if (e.key.toLowerCase() === 'd') {
        e.preventDefault();
        const w = window.open('', '_blank', 'width=900,height=600');
        if (!w) return;
        w.document.title = 'Debug Console';
        const mount = w.document.createElement('div');
        w.document.body.style.margin = '0';
        w.document.body.appendChild(mount);
        Promise.all([import('react-dom/client'), import('./components/DebugConsole')]).then(([rdom, debugModule]) => {
          const DebugConsole = debugModule.default;
          const root = (rdom as any).createRoot ? (rdom as any).createRoot(mount) : null;
          if (root) root.render(React.createElement(DebugConsole, { height: '100vh' }));
          else (rdom as any).render(React.createElement(DebugConsole, { height: '100vh' }), mount);
        });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  return (
      <GlobalErrorBoundary>
        <PageTitleProvider>
          <ScrollToTopOnNavigate />
          <TopLoader />
          <LoadingScreen visible={showLoadingScreen} message={"Loading workspace"} subtext={"Preparing your session"} />
          <AppShell>
          <BackgroundSessionWarning />
          <React.Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            {/* PUBLIC: OAuth callback must NOT be guarded */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/" element={<Protected><HomeRoute /></Protected>} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/account/security" element={<Protected><GuestAccountSecurityPage /></Protected>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route
              path="/client-portal"
              element={<ClientPortalRouteGate><ClientPortalDashboard /></ClientPortalRouteGate>}
            />
            <Route
              path="/client-portal/invoices/:invoiceId"
              element={<ClientPortalRouteGate><ClientPortalSummaryInvoice /></ClientPortalRouteGate>}
            />
            <Route
              path="/client-portal/service/:ticketId"
              element={<Protected><ServiceTicketPortalView /></Protected>}
            />
            <Route
              path="/client-portal/:eventId"
              element={<ClientPortalRouteGate><ClientPortalLanding /></ClientPortalRouteGate>}
            />
            <Route path="/s/:token" element={<PublicSignatureDocumentPage />} />
            <Route path="/t/:token" element={<PublicTemplateLandingPage />} />
            <Route path="/sign/documents/:token" element={<PublicSignatureDocumentPage />} />
            <Route path="/pay/:token" element={<PublicPaymentPage />} />
            <Route path="/venue-intake/:token" element={<PublicVenuePrecontractIntakePage />} />
            {/* Post-event questionnaires: the magic link, and Forms' preview of a draft. */}
            <Route path="/q/:token" element={<PublicPostEventPage />} />
            <Route path="/post-event/preview/:questionnaireId" element={<Protected><PostEventPreviewPage /></Protected>} />
            {/* Customer-only work order acceptance, opened from an emailed link. */}
            <Route path="/w/:token" element={<ServiceTicketAcceptancePage />} />
            <Route path="/client-portal/agreements/:id" element={<ClientPortalRouteGate><AgreementDocumentView kind="agreement" readOnly /></ClientPortalRouteGate>} />
            <Route path="/service-agreements/sign/:token" element={<AgreementDocumentView />} />
            <Route path="/service-agreements/:kind/:id" element={<Protected><AgreementDocumentView /></Protected>} />
            <Route path="/installations/sign/:token" element={<InstallationSignPage />} />
            <Route path="/installations/sign/completion/:token" element={<InstallationCompletionSignPage />} />
            <Route path="/freelancer/profile" element={<Protected><FreelancerProfilePage /></Protected>} />
            <Route path="/schedule/shared/:token" element={<ScheduleGuestLinkPage />} />
            <Route path="/schedule/time-report" element={<Protected><PageProtected pageKey="schedule"><ScheduleTimeReportPage /></PageProtected></Protected>} />
            <Route path="/schedule" element={<Protected><PageProtected pageKey="schedule"><LaborCalendarPage /></PageProtected></Protected>} />
            <Route path="/calendar" element={<Protected><PageProtected pageKey="schedule"><LaborCalendarPage /></PageProtected></Protected>} />
            <Route path="/events" element={<Protected><PageProtected pageKey="events"><EventsPage /></PageProtected></Protected>} />
            <Route path="/events/:eventId/order-chat/popout" element={<Protected><PageProtected pageKey="events"><OrderChatPopoutPage /></PageProtected></Protected>} />
            <Route path="/events/:eventId/client-view" element={<Protected><PageProtected pageKey="events"><EventClientViewPage /></PageProtected></Protected>} />
            <Route path="/events/:eventId/*" element={<Protected><PageProtected pageKey="events"><EventDetail /></PageProtected></Protected>} />
            <Route path="/events/:lookup" element={<Protected><PageProtected pageKey="events"><EventLookup /></PageProtected></Protected>} />
            <Route path="/installations" element={<Protected><PageProtected pageKey="installations"><InstallationRouteGate><InstallationsPage /></InstallationRouteGate></PageProtected></Protected>} />
            <Route path="/installations/new" element={<Protected><PageProtected pageKey="installations" required="edit"><InstallationRouteGate><InstallationsPage /></InstallationRouteGate></PageProtected></Protected>} />
            <Route path="/installations/:installationId/client-view" element={<Protected><PageProtected pageKey="installations"><InstallationRouteGate><InstallationClientViewPage /></InstallationRouteGate></PageProtected></Protected>} />
            <Route path="/installations/:installationId/*" element={<Protected><PageProtected pageKey="installations"><InstallationRouteGate><InstallationDetailPage /></InstallationRouteGate></PageProtected></Protected>} />
            <Route path="/clients" element={<Protected><PageProtected pageKey="clients"><ClientsPage /></PageProtected></Protected>} />
            <Route path="/clients/:clientId/*" element={<Protected><PageProtected pageKey="clients"><ClientDetail /></PageProtected></Protected>} />
            <Route path="/change-log" element={<Protected><PageProtected pageKey="change_log"><ChangeLogPage /></PageProtected></Protected>} />
            <Route path="/transfers" element={<Protected><PageProtected pageKey="transfers"><TransferOrdersPage /></PageProtected></Protected>} />
            {/* Old list links and stored `?poId=` / `?subrentalId=` notification links land on the records. */}
            <Route path="/transfers/purchase-orders" element={<LegacyProcurementRedirect kind="purchase_orders" />} />
            <Route path="/transfers/purchase-orders/:poId/*" element={<Protected><PageProtected pageKey="transfers"><FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES['purchase-orders']}><PurchaseOrderRecordPage /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/transfers/xrental-orders" element={<LegacyProcurementRedirect kind="subrentals" />} />
            <Route path="/transfers/xrental-orders/:recordId/*" element={<Protected><PageProtected pageKey="transfers"><FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES['xrental-orders']}><SubrentalRecordPage /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/transfers/:transferId/*" element={<Protected><PageProtected pageKey="transfers"><TransferOrderDetail /></PageProtected></Protected>} />
            <Route path="/service" element={<Protected><PageProtected pageKey="service"><FeatureProtected featureKey="service.tickets"><ServiceTicketsPage /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/service/new" element={<Protected><PageProtected pageKey="service" required="edit"><FeatureProtected featureKey="service.tickets" required="edit_own"><ServiceTicketCreatePage /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/service/scan" element={<Protected><PageProtected pageKey="service"><FeatureProtected featureKey="service.scan"><ServiceScanPage /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/service/:ticketId/*" element={<Protected><PageProtected pageKey="service"><FeatureProtected featureKey="service.tickets"><ServiceTicketDetail /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/signature-documents" element={<Protected><PageProtected pageKey="signatures"><FeatureProtected featureKey="signatures.library"><SignatureDocumentsPage /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/signature-documents/:documentId/edit" element={<Protected><PageProtected pageKey="signatures" required="edit"><FeatureProtected featureKey="signatures.library" required="edit_own"><SignatureDocumentEditorPage /></FeatureProtected></PageProtected></Protected>} />
            {/* Signing without a link: the owner signing their own document, or a
                customer signing on the owner's device. */}
            <Route path="/signature-documents/:documentId/sign" element={<Protected><PageProtected pageKey="signatures" required="edit"><FeatureProtected featureKey="signatures.library" required="edit_own"><SignatureDocumentSignInPersonPage /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/invoicing" element={<Protected><PageProtected pageKey="invoicing"><InvoicingPage /></PageProtected></Protected>} />
            <Route path="/invoicing/invoices/:invoiceId/*" element={<Protected><PageProtected pageKey="invoicing"><InvoiceRecordPage /></PageProtected></Protected>} />
            <Route path="/invoicing/master-bills/:summaryId/*" element={<Protected><PageProtected pageKey="invoicing"><MasterBillRecordPage /></PageProtected></Protected>} />
            <Route path="/yearly-inventory/campaigns/:campaignId/workspace/*" element={<Protected><PageProtected pageKey="yearly_inventory"><YearlyInventoryWorkspacePage /></PageProtected></Protected>} />
            <Route path="/yearly-inventory/campaigns/:campaignId/review" element={<Protected><PageProtected pageKey="yearly_inventory" required="edit"><YearlyInventoryReviewPage /></PageProtected></Protected>} />
            <Route path="/yearly-inventory/campaigns/:campaignId/planned-purchases" element={<Protected><PageProtected pageKey="yearly_inventory" required="edit"><YearlyInventoryPlannedPurchasesPage /></PageProtected></Protected>} />
            <Route path="/yearly-inventory/campaigns/:campaignId/planned-transfers" element={<Protected><PageProtected pageKey="yearly_inventory" required="edit"><YearlyInventoryPlannedTransfersPage /></PageProtected></Protected>} />
            <Route path="/yearly-inventory/rollups/:rollupId" element={<Protected><PageProtected pageKey="yearly_inventory"><YearlyInventoryRollupPage /></PageProtected></Protected>} />
            <Route path="/yearly-inventory/campaigns/:campaignId" element={<Protected><PageProtected pageKey="yearly_inventory"><YearlyInventoryCampaignsPage /></PageProtected></Protected>} />
            <Route path="/yearly-inventory" element={<Protected><LegacyInventoryRedirect kind="yearly" /></Protected>} />
            <Route path="/inventory" element={<Protected><InventoryLibraryPage /></Protected>} />
            <Route path="/inventory/counts/:runId/:section?" element={<Protected><PageProtected pageKey="inventory"><CountRecordPage /></PageProtected></Protected>} />
            <Route path="/inventory/items/:itemId/:section?" element={<Protected><PageProtected pageKey="admin_inventory"><ItemRecordPage /></PageProtected></Protected>} />
            <Route path="/inventory/kits/:kitId/:section?" element={<Protected><PageProtected pageKey="admin_inventory"><KitRecordPage /></PageProtected></Protected>} />
            <Route path="/inventory/assets/:assetId/:section?" element={<Protected><PageProtected pageKey="admin_inventory"><AssetRecordPage /></PageProtected></Protected>} />
            <Route path="/inventory-runs/:runId" element={<Protected><PageProtected pageKey="inventory"><InventoryRunDetailPage /></PageProtected></Protected>} />
            <Route path="/profile" element={<Protected><UserProfilePage /></Protected>} />
            <Route path="/profile/edit" element={<Protected><EditProfilePage /></Protected>} />
            <Route path="/venues" element={<Protected><PageProtected pageKey="venues"><VenuesPage /></PageProtected></Protected>} />
            <Route path="/venues/:venueId" element={<Protected><PageProtected pageKey="venues"><VenueDetail /></PageProtected></Protected>} />
            <Route path="/todo" element={<Protected><PageProtected pageKey="planner"><UserTodoPage /></PageProtected></Protected>} />
            <Route path="/admin/login-events" element={<Navigate to="/admin/users?tab=logins" replace />} />
            <Route path="/admin/settings" element={<Protected><PageProtected pageKey="settings"><AdminAISettingsPage /></PageProtected></Protected>} />
            {/* Inventory Catalog and Maintenance › Kits, Assets, Cables & Adapters moved into the Inventory
                library (docs/inventory.md); their old links land on its tabs and records. */}
            <Route path="/admin/inventory" element={<Protected><LegacyInventoryRedirect kind="catalog" /></Protected>} />
            <Route path="/admin/kits" element={<Protected><LegacyInventoryRedirect kind="kits" /></Protected>} />
            <Route path="/admin/assets" element={<Protected><LegacyInventoryRedirect kind="assets" /></Protected>} />
            <Route path="/admin/stock" element={<Protected><LegacyInventoryRedirect kind="stock" /></Protected>} />
            <Route path="/admin/alerts" element={<Protected><LegacyInventoryRedirect kind="alerts" /></Protected>} />
            <Route path="/admin/history" element={<Navigate to="/admin/settings?tab=history" replace />} />
            <Route path="/admin/security" element={<Navigate to="/admin/settings?tab=security" replace />} />
            <Route path="/admin/venues" element={<Navigate to="/admin/history" replace />} />
            <Route path="/admin/todo" element={<Protected><SuperadminTodoPage /></Protected>} />
            <Route path="/admin/suggestions" element={<Protected><SuggestionsInboxRedirect /></Protected>} />
            {/* Editor routes outside the maintenance layout (full-page editors) */}
            <Route path="/admin/products/editor" element={<Protected><PageProtected pageKey="maintenance" required="edit"><FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.products} required="edit_own"><AdminProductEditorPage /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/admin/items/editor" element={<Protected><PageProtected pageKey="maintenance" required="edit"><FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.items} required="edit_own"><AdminItemEditorPage /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/admin/assets/editor" element={<Protected><PageProtected pageKey="maintenance" required="edit"><FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.assets} required="edit_own"><AdminAssetEditorPage /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/admin/stock/editor" element={<Protected><LegacyInventoryRedirect kind="stock-editor" /></Protected>} />
            <Route path="/admin/locations/editor" element={<Protected><PageProtected pageKey="maintenance" required="edit"><FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.locations} required="edit_own"><AdminLocationEditorPage /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/admin/categories/editor" element={<Protected><PageProtected pageKey="maintenance" required="edit"><FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.categories} required="edit_own"><AdminCategoryEditorPage /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/admin/forms/new" element={<Protected><PageProtected pageKey="forms" required="edit"><FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.forms} required="edit_own"><AdminFormBuilderPage /></FeatureProtected></PageProtected></Protected>} />
            <Route path="/admin/forms/:formId/edit" element={<Protected><PageProtected pageKey="forms" required="edit"><FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.forms} required="edit_own"><AdminFormBuilderPage /></FeatureProtected></PageProtected></Protected>} />
            {/* Maintenance › Purchase Orders and Vendor Rental Inventory moved to Warehouse; their old
                links (and ?poId= / ?subrentalId= / ?itemId= deep links) land on the new tabs and records. */}
            <Route path="/admin/purchase-orders" element={<LegacyProcurementRedirect kind="purchase_orders" />} />
            <Route path="/admin/xrental-orders" element={<LegacyProcurementRedirect kind="subrentals" />} />
            {/* Maintenance section with persistent layout */}
            <Route path="/admin" element={<Protected><PageProtected pageKey="maintenance"><MaintenanceLayout /></PageProtected></Protected>}>
              <Route path="maintenance" element={<AdminMaintenancePage />} />
              <Route path="users" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.users}><AdminUsersPage /></FeatureProtected>} />
              <Route path="products" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.products}><AdminProductsPage /></FeatureProtected>} />
              <Route path="items" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.items}><AdminItemsPage /></FeatureProtected>} />
              <Route path="items/installation" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.items}><AdminInstallationCatalogPage /></FeatureProtected>} />
              <Route path="items-nonserial" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES['items-nonserial']}><AdminNonSerialItemsPage /></FeatureProtected>} />
              <Route path="charges" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.charges}><AdminChargesPage /></FeatureProtected>} />
              <Route path="pricing" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.pricing}><AdminPricingMaintenancePage /></FeatureProtected>} />
              <Route path="accounting-gl" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES['accounting-gl']}><AdminAccountingGlPage /></FeatureProtected>} />
              <Route path="invoicing" element={<Navigate to="/invoicing" replace />} />
              <Route path="labor" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.labor}><AdminLaborPage /></FeatureProtected>} />
              <Route path="service" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.service}><AdminServicePage /></FeatureProtected>} />
              <Route path="locations" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.locations}><AdminLocationsPage /></FeatureProtected>} />
              <Route path="sections" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.sections}><AdminSectionsPage /></FeatureProtected>} />
              <Route path="event-stages" element={<Navigate to="/admin/settings?tab=event-stages" replace />} />
              <Route path="items-panel-columns" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES['items-panel-columns']}><AdminItemsPanelColumnsPage /></FeatureProtected>} />
              <Route path="categories" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.categories}><AdminCategoriesPage /></FeatureProtected>} />
              <Route path="tax-codes" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES['tax-codes']}><AdminTaxCodesPage /></FeatureProtected>} />
              <Route path="terms-and-conditions" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES['terms-and-conditions']}><AdminTermsAndConditionsPage /></FeatureProtected>} />
              <Route path="payment-terms" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES['payment-terms']}><AdminPaymentTermsPage /></FeatureProtected>} />
              <Route path="freelancers" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.freelancers}><AdminFreelancersPage /></FeatureProtected>} />
              <Route path="vendors" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.vendors}><AdminVendorsPage /></FeatureProtected>} />
              <Route path="forms" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES.forms}><AdminFormsPage /></FeatureProtected>} />
              <Route path="venue-precontract-form" element={<FeatureProtected featureKey={MAINTENANCE_ROUTE_FEATURES['venue-precontract-form']} required="edit_all"><AdminVenuePrecontractFormPage /></FeatureProtected>} />
            </Route>
            </Routes>
          </React.Suspense>
            {showDebug ? (
              <React.Suspense fallback={null}>
                <DebugOverlay supabaseUrl={cfg.supabaseUrl} />
              </React.Suspense>
            ) : null}
          </AppShell>
          {/* AI Venue Search heartbeat monitor moved to NotificationBell dropdown */}
          {/* Vercel Analytics: collects page views and SPA navigations */}
          <Analytics />
          {/* Vercel Speed Insights: field metrics + web vitals */}
          <SpeedInsights />
        </PageTitleProvider>
      </GlobalErrorBoundary>
  );
}

function EventLookup() {
  const supabase = supabaseClient;
  const { lookup } = useParams();
  const { search } = useLocation();
  const [resolving, setResolving] = React.useState(true);
  const [target, setTarget] = React.useState<string | null>(null);
  const [lookupError, setLookupError] = React.useState<string | null>(null);
  React.useEffect(()=>{
    let active = true;
    (async ()=>{
      setLookupError(null);
      try {
        if(!lookup) return;
        // If looks like a UUID just go directly
        const uuidRegex = /^[0-9a-fA-F-]{32,36}$/;
        if(uuidRegex.test(lookup)){ if (active) setTarget(lookup); return; }
        // A query failure is distinct from a legitimate no-match result.
        const byCode = await runSupabaseQuery(
          () => supabase.from('events').select('id').ilike('event_code', lookup).maybeSingle(),
          'Unable to resolve this event.',
          'events.lookup.code',
        );
        if(active && byCode){ setTarget(byCode.id); return; }
        const byR2 = await runSupabaseQuery(
          () => supabase.from('events').select('id').ilike('r2_contract_iid', lookup).maybeSingle(),
          'Unable to resolve this event.',
          'events.lookup.r2',
        );
        if(active && byR2) setTarget(byR2.id);
      } catch (error) {
        if (active) setLookupError(safeErrorMessage(error, 'Unable to resolve this event.'));
      } finally {
        if (active) setResolving(false);
      }
    })();
    return ()=>{ active=false; };
  }, [lookup, supabase]);
  if(resolving) return <div className="section-shell"><p className="fg-muted text-sm">Resolving event…</p></div>;
  if(lookupError) return <div className="section-shell"><p className="error">{lookupError}</p></div>;
  if(!target) return <div className="section-shell"><p className="error">Event not found for '{lookup}'.</p></div>;
  // Keep the query: links such as /events/<id>?post-event=open (the post-event low-rating alert) land here first.
  return <Navigate to={{ pathname: `/events/${target}/overview`, search: search }} replace />;
}

function PrincipalRuntime({ cfg }: { cfg: BootConfig }) {
  const principalEpoch = useSessionStore((state) => state.principalEpoch);
  const queryClient = React.useMemo(createPrincipalQueryClient, [principalEpoch]);

  React.useEffect(() => {
    const unregister = setActivePrincipalQueryClient(queryClient);
    return () => {
      unregister();
      void queryClient.cancelQueries();
      queryClient.clear();
    };
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient} key={principalEpoch}>
      <JobHubProvider key={principalEpoch}>
        <Boot cfg={cfg} />
      </JobHubProvider>
    </QueryClientProvider>
  );
}

export function mount(el: HTMLElement, cfg: BootConfig) {
  installProtectedAppFetchGuard();
  const root = createRoot(el);
  root.render(
    <BrowserRouter>
      <AuthSessionCoordinator supabase={supabaseClient} disabled={Boolean(DEV_AUTH_EMAIL)}>
        <PrincipalRuntime cfg={cfg} />
      </AuthSessionCoordinator>
    </BrowserRouter>,
  );
}

// Auto-mount if a div#site-survey-root exists (standalone hosting)
const auto = document.getElementById('site-survey-root');
const g = (window as any).SiteSurveyConfig || {};
const previewUrl = (import.meta as any).env?.NEXT_PUBLIC_SUPABASE_URL;
const previewKey = (import.meta as any).env?.NEXT_PUBLIC_SUPABASE_ANON_KEY;
const envUrl = previewUrl || (import.meta as any).env?.VITE_SUPABASE_URL;
const envKey = previewKey || (import.meta as any).env?.VITE_SUPABASE_ANON_KEY;
if (auto) {
  if (g.supabaseUrl && g.supabaseAnonKey) {
    mount(auto, { supabaseUrl: g.supabaseUrl, supabaseAnonKey: g.supabaseAnonKey });
  } else if (envUrl && envKey) {
    // Fallback to build-time env vars
    mount(auto, { supabaseUrl: envUrl, supabaseAnonKey: envKey });
  } else {
    console.error('Site Survey: Missing Supabase configuration. Provide env vars or window.SiteSurveyConfig.');
  }
}
